import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiBriefcase, FiUsers, FiClock } from 'react-icons/fi';

const Unternehmen = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTimeout(() => {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 800);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Intro Header */}
      <div className="bg-slate-900 text-white py-16 md:py-24">
        <div className="container-custom max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Personalbedarf anfragen</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Sie suchen Aushilfen, Minijobber oder flexible Unterstützung? Wir vermitteln passende Bewerber für einfache und wiederkehrende Tätigkeiten im Norden.
          </p>
        </div>
      </div>

      {/* Value Props */}
      <div className="border-b border-slate-100 bg-slate-50 py-10">
        <div className="container-custom max-w-5xl">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-teal-600">
                <SafeIcon icon={FiUsers} className="text-xl" />
              </div>
              <h3 className="font-semibold mb-2">Regionale Bewerber</h3>
              <p className="text-sm text-slate-600">Wir fokussieren uns auf Kandidaten aus Hamburg und Umgebung.</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-teal-600">
                <SafeIcon icon={FiClock} className="text-xl" />
              </div>
              <h3 className="font-semibold mb-2">Schnelle Vermittlung</h3>
              <p className="text-sm text-slate-600">Oft können wir innerhalb von 48 Stunden erste Profile vorstellen.</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-teal-600">
                <SafeIcon icon={FiBriefcase} className="text-xl" />
              </div>
              <h3 className="font-semibold mb-2">Vorauswahl inklusive</h3>
              <p className="text-sm text-slate-600">Wir filtern nach Verfügbarkeit und Eignung, um Ihnen Zeit zu sparen.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="py-16 md:py-20">
        <div className="container-custom max-w-3xl">
          {submitted ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-50 p-10 rounded-2xl text-center border border-slate-200"
            >
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiCheckCircle} className="text-4xl text-teal-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Vielen Dank für Ihre Anfrage.</h2>
              <p className="text-slate-600 text-lg mb-8">
                Wir haben Ihren Personalbedarf erhalten. Ein persönlicher Ansprechpartner wird sich zeitnah bei Ihnen melden, um die nächsten Schritte rund um passende Kandidaten abzustimmen.
              </p>
              <button 
                onClick={() => setSubmitted(false)}
                className="btn-outline"
              >
                Weitere Anfrage stellen
              </button>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
            >
              <div className="p-6 md:p-10">
                <h2 className="text-2xl font-bold mb-8 text-center">Teilen Sie uns Ihren Bedarf mit</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Info */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Firmenname *</label>
                      <input type="text" required className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Ansprechpartner *</label>
                      <input type="text" required className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail *</label>
                      <input type="email" required className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Standort / PLZ des Einsatzortes *</label>
                      <input type="text" required placeholder="z.B. Hamburg, 20354" className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500" />
                    </div>
                  </div>

                  <hr className="my-8 border-slate-100" />

                  {/* Job Details */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Gesuchte Tätigkeit / Jobtitel *</label>
                      <input type="text" required placeholder="z.B. Aushilfe im Verkauf (m/w/d)" className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500" />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Anzahl benötigter Personen</label>
                        <select className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white">
                          <option>1 Person</option>
                          <option>2-5 Personen</option>
                          <option>Mehr als 5 Personen</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Einsatzbeginn</label>
                        <select className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white">
                          <option>Ab sofort / Kurzfristig</option>
                          <option>Innerhalb der nächsten 2 Wochen</option>
                          <option>Nächster Monat</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Kurze Beschreibung der Tätigkeit & Anforderungen *</label>
                      <textarea rows="4" required placeholder="Welche Aufgaben sollen übernommen werden? Welche Arbeitszeiten sind geplant? (Minijob, Teilzeit etc.)" className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500"></textarea>
                    </div>
                  </div>

                  {/* Consent */}
                  <div className="pt-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" required className="mt-1 text-teal-600 focus:ring-teal-500 rounded" />
                      <span className="text-sm text-slate-600">
                        Ich stimme der Datenschutzerklärung zu und erlaube Nebenjob Nord, mich zur Klärung des Personalbedarfs per E-Mail zu kontaktieren. *
                      </span>
                    </label>
                  </div>

                  <button type="submit" className="w-full btn-secondary text-lg py-4 bg-slate-900 text-white hover:bg-slate-800 border-none">
                    Unverbindlich anfragen
                  </button>
                  <p className="text-center text-xs text-slate-500 mt-4">
                    Die Anfrage ist für Unternehmen kostenlos. Kosten entstehen erst bei erfolgreicher Vermittlung nach vorheriger Absprache.
                  </p>
                </form>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Unternehmen;